import React from 'react';
import { useTranslation } from 'react-i18next';
import Gap from '../../interfaces/gap.interface';
import Layer from '../../interfaces/layer.interface';
import TestId from '../../interfaces/test-id.interface';
import { getCurrentLanguage } from '../../utils/i18n/i18n.util';
import Dropdown from '../dropdown/dropdown.component';
import Icon from '../icon/icon.component';

export interface Props extends Layer, Gap, TestId {
  /** The ISO 639-1 language codes to render as options */
  languages: string[];
}

/** Renders a language icon + name */
const Lang = ({
  className,
  icon,
  name,
  onClick,
}: {
  className: string;
  icon: string;
  name: string;
  onClick?: (event?: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
}): React.ReactElement => (
  <div
    className={`flex items-center ${className}`}
    onClick={onClick}
    onKeyPress={(): void => onClick && onClick()}
    role="button"
    tabIndex={0}
  >
    <Icon name={icon} /> {name}
  </div>
);

/** A component to switch the current language */
const LangSwitch = ({ gap = 'none', layer = 3, languages, testId }: Props): React.ReactElement => {
  const [t, i18n] = useTranslation();
  const language = getCurrentLanguage();

  const setLanguage$ = (lang: string): Promise<import('i18next').TFunction> =>
    i18n.changeLanguage(lang);

  return (
    <div
      className={`
        absolute
        bg-white
        px-2
        py-1
        right-0
        text-sm
        top-8
        gap--${gap}
        layer--${layer}
      `}
      data-testid={testId}
    >
      <Dropdown
        position="right"
        trigger={<Lang className="pl-1 pr-2" icon={language} name={language} />}
      >
        {languages.sort().map((lang) => (
          <Lang
            className="py-1"
            key={lang}
            icon={lang}
            name={t(`lang.${lang}`)}
            onClick={(): void => {
              setLanguage$(lang);
            }}
          />
        ))}
      </Dropdown>
    </div>
  );
};

export default LangSwitch;
